import axios from 'axios';
import { useState, useEffect, useRef, useMemo } from 'react';
import { backendClient } from '@/lib/api-client';

export type EcaProgressParams = {
  fileReferenceGuids: string[];
  folderGuids: string[];
} | null;

interface MatterMemoryProgress {
  total_eligible_documents: number;
  processed_documents: number;
  remaining_documents: number;
  percentage_complete: number;
  is_complete: boolean;
  is_processing: boolean;
}

const FRESHNESS_VALUES = [
  'running',
  'pending',
  'stale',
  'up_to_date',
  'needs_generation',
  'no_documents',
] as const;

export type FreshnessStatus = (typeof FRESHNESS_VALUES)[number];

const isFreshnessStatus = (value: unknown): value is FreshnessStatus =>
  typeof value === 'string' && (FRESHNESS_VALUES as readonly string[]).includes(value);

const isRequestCanceled = (error: unknown): boolean => {
  if (axios.isCancel(error)) return true;
  const name = (error as { name?: unknown })?.name;
  return name === 'CanceledError' || name === 'AbortError';
};

const GENERATION_START_TIMEOUT_MS = 2 * 60 * 1000;

interface UseMatterMemoryReturn {
  matterMemory: string | null;
  progress: MatterMemoryProgress | null;
  /** Suspend the 5s progress polling loop (the first GET still fires). */
  pollingEnabled: boolean;
  setPollingEnabled: (enabled: boolean) => void;
  /** Distinct documents linked to this extraction. */
  contributingDocumentsCount: number | null;
  /** ECA: persisted explicit file_reference GUIDs. */
  ecaExplicitFileReferenceGuids: string[] | null;
  /** ECA: persisted explicit folder GUIDs. */
  ecaExplicitFolderGuids: string[] | null;
  /** ECA: count of persisted picks that have since gone stale. */
  ecaDroppedPersistedPickCount: number | null;
  /** ECA: effective document set (Client Documents + explicit picks) exceeds the per-matter cap. */
  ecaOverDocumentLimit: boolean;
  /** ECA: hard cap on the effective document set; `null` for non-ECA. */
  ecaDocumentLimit: number | null;
  /** ECA: size of the effective document set; `null` for non-ECA or when unknown. */
  ecaEffectiveDocumentCount: number | null;
  /** `null` until the first GET completes. */
  freshnessStatus: FreshnessStatus | null;
  /** Raw `should_trigger_generation` reason; useful for telemetry. */
  generationReason: string | null;
  loadingSummary: boolean;
  summaryError: string | null;
  isGenerating: boolean;
  fetchMatterMemory: () => Promise<void>;
  fetchProgress: () => Promise<void>;
  markGenerationPending: () => void;
}

/**
 * Custom hook to manage matter memory fetching and progress tracking.
 * Handles self-healing auto-trigger, progress polling, and state management.
 *
 * @param matterGuid - The GUID of the matter to fetch memory for
 * @returns Object containing matter memory data, progress, loading states, and fetch functions
 */
export const useMatterMemory = (
  matterGuid: string | undefined,
  extractionType = 'matter_memory',
  ecaProgressParams: EcaProgressParams = null,
  initialPollingEnabled = true,
): UseMatterMemoryReturn => {
  const [matterMemory, setMatterMemory] = useState<string | null>(null);
  const [contributingDocumentsCount, setContributingDocumentsCount] = useState<number | null>(null);
  const [ecaExplicitFileReferenceGuids, setEcaExplicitFileReferenceGuids] = useState<string[] | null>(null);
  const [ecaExplicitFolderGuids, setEcaExplicitFolderGuids] = useState<string[] | null>(null);
  const [ecaDroppedPersistedPickCount, setEcaDroppedPersistedPickCount] = useState<number | null>(null);
  const [ecaOverDocumentLimit, setEcaOverDocumentLimit] = useState<boolean>(false);
  const [ecaDocumentLimit, setEcaDocumentLimit] = useState<number | null>(null);
  const [ecaEffectiveDocumentCount, setEcaEffectiveDocumentCount] = useState<number | null>(null);
  const [freshnessStatus, setFreshnessStatus] = useState<FreshnessStatus | null>(null);
  const [generationReason, setGenerationReason] = useState<string | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [progress, setProgress] = useState<MatterMemoryProgress | null>(null);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [pollingEnabled, setPollingEnabled] = useState(initialPollingEnabled);
  const [generationStartPending, setGenerationStartPending] = useState(false);
  const prevProgressRef = useRef<MatterMemoryProgress | null>(null);

  // One AbortController per in-flight fetcher, aborted before each new fetch.
  const progressAbortRef = useRef<AbortController | null>(null);
  const memoryAbortRef = useRef<AbortController | null>(null);

  // Always-current picks, read by the fetchers instead of the closed-over prop.
  // A fetcher invoked from a stale callback (e.g. `triggerEarlyCaseGenerate`
  // captured before the selection was saved) would otherwise GET with the
  // previous picks and clobber the limit flags with stale state.
  const ecaProgressParamsRef = useRef(ecaProgressParams);
  ecaProgressParamsRef.current = ecaProgressParams;

  const ecaProgressKey = useMemo(() => {
    if (extractionType !== 'early_case_assessment' || !ecaProgressParams) {
      return '';
    }
    return `${ecaProgressParams.fileReferenceGuids.join('\0')}\n${ecaProgressParams.folderGuids.join('\0')}`;
  }, [extractionType, ecaProgressParams]);

  const fetchProgress = async () => {
    if (!matterGuid) return;

    progressAbortRef.current?.abort();
    const controller = new AbortController();
    progressAbortRef.current = controller;

    setLoadingProgress(true);
    try {
      let url = `/matter-memory/${matterGuid}/progress`;
      const currentEcaParams = ecaProgressParamsRef.current;
      if (extractionType === 'early_case_assessment' && currentEcaParams) {
        const params = new URLSearchParams();
        currentEcaParams.fileReferenceGuids.forEach((g) => params.append('eca_file_reference_guid', g));
        currentEcaParams.folderGuids.forEach((g) => params.append('eca_folder_guid', g));
        const qs = params.toString();
        if (qs) {
          url = `${url}?${qs}`;
        }
      }
      const response = await backendClient.get(url, {
        headers: {
          extraction_type: extractionType,
        },
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      setProgress(response.data);
      if (response.data.is_processing) {
        setGenerationStartPending(false);
      }
    } catch (error: any) {
      if (isRequestCanceled(error)) return;
      // Silently handle 404 - endpoint may not be available yet if backend hasn't restarted
      if (error.response?.status !== 404) {
        console.error('Error fetching progress:', error);
      }
    } finally {
      if (progressAbortRef.current === controller) {
        progressAbortRef.current = null;
      }
      if (!controller.signal.aborted) {
        setLoadingProgress(false);
      }
    }
  };

  const fetchMatterMemory = async () => {
    if (!matterGuid) return;

    memoryAbortRef.current?.abort();
    const controller = new AbortController();
    memoryAbortRef.current = controller;

    setLoadingSummary(true);
    setSummaryError(null);
    try {
      let url = `/matter-memory/${matterGuid}`;
      const currentEcaParams = ecaProgressParamsRef.current;
      if (extractionType === 'early_case_assessment' && currentEcaParams) {
        const params = new URLSearchParams();
        currentEcaParams.fileReferenceGuids.forEach((g) => params.append('eca_file_reference_guid', g));
        currentEcaParams.folderGuids.forEach((g) => params.append('eca_folder_guid', g));
        const qs = params.toString();
        if (qs) {
          url = `${url}?${qs}`;
        }
      }

      const response = await backendClient.get(url, {
        headers: {
          extraction_type: extractionType,
        },
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      let summary = response.data.summary;
      const rawContributing = response.data.contributing_documents_count;
      setContributingDocumentsCount(typeof rawContributing === 'number' ? rawContributing : null);

      const rawEcaFileRefs = response.data.explicit_file_reference_guids;
      setEcaExplicitFileReferenceGuids(Array.isArray(rawEcaFileRefs) ? rawEcaFileRefs : null);
      const rawEcaFolderGuids = response.data.explicit_folder_guids;
      setEcaExplicitFolderGuids(Array.isArray(rawEcaFolderGuids) ? rawEcaFolderGuids : null);
      const rawDroppedCount = response.data.dropped_persisted_pick_count;
      setEcaDroppedPersistedPickCount(
        typeof rawDroppedCount === 'number' ? rawDroppedCount : null,
      );
      const rawOverLimit = response.data.over_document_limit;
      setEcaOverDocumentLimit(typeof rawOverLimit === 'boolean' ? rawOverLimit : false);
      const rawDocLimit = response.data.document_limit;
      setEcaDocumentLimit(typeof rawDocLimit === 'number' ? rawDocLimit : null);
      const rawEffectiveCount = response.data.effective_document_count;
      setEcaEffectiveDocumentCount(
        typeof rawEffectiveCount === 'number' ? rawEffectiveCount : null,
      );

      const rawFreshness = response.data.freshness_status;
      setFreshnessStatus(isFreshnessStatus(rawFreshness) ? rawFreshness : null);
      const rawReason = response.data.generation_reason;
      setGenerationReason(typeof rawReason === 'string' ? rawReason : null);

      // Strip markdown code fence if present (e.g., ```markdown ... ```)
      if (summary) {
        summary = summary.replace(/^```markdown\n?/i, '').replace(/\n?```$/, '');
      }

      setMatterMemory(summary);

      // Kick progress immediately so the polling effect can pick it up.
      const justQueuedOrRunning =
        rawFreshness === 'pending' || rawFreshness === 'running';
      if (justQueuedOrRunning) {
        fetchProgress();
      }
    } catch (error: any) {
      if (isRequestCanceled(error)) return;
      console.error('Error fetching matter memory:', error);
      setSummaryError(error.response?.data?.detail || 'Failed to load summary');
    } finally {
      if (memoryAbortRef.current === controller) {
        memoryAbortRef.current = null;
      }
      if (!controller.signal.aborted) {
        setLoadingSummary(false);
      }
    }
  };

  const markGenerationPending = () => {
    setMatterMemory(null);
    setFreshnessStatus('pending');
    setGenerationStartPending(true);
    setProgress((current) => current
      ? {
          ...current,
          processed_documents: 0,
          remaining_documents: current.total_eligible_documents,
          percentage_complete: 0,
          is_complete: false,
          is_processing: false,
        }
      : null);
  };

  useEffect(() => {
    if (matterGuid) {
      fetchMatterMemory();
      fetchProgress();
    }
  }, [matterGuid, extractionType, ecaProgressKey]);

  // Abort in-flight requests on unmount.
  useEffect(() => {
    return () => {
      progressAbortRef.current?.abort();
      memoryAbortRef.current?.abort();
    };
  }, []);

  // An accepted Celery task can sit in the queue before acquiring its Redis
  // semaphore. Keep polling through that gap even if the old extraction still
  // reports complete/up-to-date. Once processing is observed, normal progress
  // polling takes over. The timeout prevents a lost broker task from leaving
  // the UI permanently locked.
  useEffect(() => {
    if (!generationStartPending) return;
    const timeout = setTimeout(
      () => setGenerationStartPending(false),
      GENERATION_START_TIMEOUT_MS,
    );
    return () => clearTimeout(timeout);
  }, [generationStartPending]);

  // Poll progress every 5s while running or queued.
  const shouldPoll =
    pollingEnabled &&
    (
      generationStartPending
      || Boolean(progress?.is_processing)
      || freshnessStatus === 'pending'
    );
  useEffect(() => {
    if (!matterGuid || !shouldPoll) return;

    const interval = setInterval(() => {
      fetchProgress();
    }, 5000);

    return () => clearInterval(interval);
  }, [matterGuid, extractionType, ecaProgressKey, shouldPoll]);

  // Fetch summary when processing completes or makes progress
  useEffect(() => {
    // Skip if this is the initial render
    if (progress === null) return;

    const prevProgress = prevProgressRef.current;

    // Fetch the updated summary when:
    // 1. Processing flag changes from true to false
    // 2. is_complete changes from false to true
    // 3. remaining_documents reaches 0
    // 4. processed_documents increases (batch completed while still processing)
    const wasProcessing = prevProgress?.is_processing === true;
    const isNowNotProcessing = progress.is_processing === false;
    const justCompleted = !prevProgress?.is_complete && progress.is_complete;
    const allDocsProcessed = prevProgress && prevProgress.remaining_documents > 0 && progress.remaining_documents === 0;
    const madeProgress = prevProgress && progress.processed_documents > prevProgress.processed_documents;

    if ((wasProcessing && isNowNotProcessing) || justCompleted || allDocsProcessed || madeProgress) {
      fetchMatterMemory();
    }

    // Update ref for next render
    prevProgressRef.current = progress;
  }, [progress]);

  const isGenerating =
    generationStartPending
    || Boolean(progress?.is_processing)
    || freshnessStatus === 'running'
    || freshnessStatus === 'pending';

  return {
    matterMemory,
    progress,
    pollingEnabled,
    setPollingEnabled,
    contributingDocumentsCount,
    ecaExplicitFileReferenceGuids,
    ecaExplicitFolderGuids,
    ecaDroppedPersistedPickCount,
    ecaOverDocumentLimit,
    ecaDocumentLimit,
    ecaEffectiveDocumentCount,
    freshnessStatus,
    generationReason,
    loadingSummary,
    summaryError,
    isGenerating,
    fetchMatterMemory,
    fetchProgress,
    markGenerationPending,
  };
};
